import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import { ROUTES } from "config/navConfig";

// ESG alerts — cross-referenced to canonical MOCK_ASSETS IDs
const alerts = [
  {
    id: 1,
    title: "Biodiversity Net Gain Below Threshold",
    project: "Tyne Bridge Toll Road",
    assetId: "tyne-bridge",
    severity: "warning",
    icon: "AlertTriangle",
    detail: "Biodiversity net gain below threshold — Tyne Bridge Toll Road. Remediation plan required by 31/03/2026.",
    date: "28/09/2025",
  },
  {
    id: 2,
    title: "SFDR Article 9 Compliance Confirmed",
    project: "Solano Wind Farm SPV",
    assetId: "solano-wind",
    severity: "success",
    icon: "CheckCircle",
    detail: "Annual sustainability disclosure submitted and verified by external auditor.",
    date: "28/02/2026",
  },
  {
    id: 3,
    title: "LTIFR Spike Detected",
    project: "Elbe Biomass Plant",
    assetId: "elbe-biomass",
    severity: "warning",
    icon: "AlertTriangle",
    detail: "LTIFR spike detected — Elbe Biomass Plant Q3 2025. Incident investigation report pending from site HSE manager.",
    date: "15/10/2025",
  },
  {
    id: 4,
    title: "Board Diversity Target Missed",
    project: "Porto Digital Hub",
    assetId: "porto-digital",
    severity: "info",
    icon: "Info",
    detail: "Board diversity target of 50% not met (currently 38%) — Porto Digital Hub. Recruitment process underway.",
    date: "01/10/2025",
  },
];

const severityConfig = {
  warning: {
    color: "var(--color-warning)",
    bg: "rgba(217,119,6,0.08)",
    border: "rgba(217,119,6,0.2)",
    badge: "Warning",
  },
  success: {
    color: "var(--color-success)",
    bg: "rgba(5,150,105,0.06)",
    border: "rgba(5,150,105,0.15)",
    badge: "Compliant",
  },
  info: {
    color: "var(--color-secondary)",
    bg: "rgba(74,107,138,0.06)",
    border: "rgba(74,107,138,0.15)",
    badge: "Info",
  },
};

export default function ESGAlertsPanel() {
  const navigate = useNavigate();

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Leaf" size={18} color="var(--color-success)" />
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: "1rem" }}>
            ESG Alerts &amp; Compliance
          </h3>
        </div>
        <span
          className="flex items-center gap-1 px-2 py-0.5 rounded-full font-caption font-medium"
          style={{ fontSize: "0.6875rem", background: "rgba(217,119,6,0.1)", color: "var(--color-warning)" }}
        >
          <Icon name="AlertTriangle" size={11} />
          1 Action Required
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
        {alerts?.map((alert) => {
          const sc = severityConfig?.[alert?.severity];
          return (
            <div
              key={alert?.id}
              className="group flex gap-3 px-5 py-4 hover:bg-muted transition-all duration-250 cursor-pointer"
              style={{ borderBottom: "1px solid var(--color-border)" }}
              onClick={() => navigate(ROUTES.esgReporting)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(ROUTES.esgReporting)}
            >
              <div
                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md mt-0.5"
                style={{ background: sc?.bg }}
              >
                <Icon name={alert?.icon} size={16} color={sc?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-caption font-semibold text-foreground text-sm leading-tight group-hover:underline">
                    {alert?.title}
                  </p>
                  <span
                    className="flex-shrink-0 px-1.5 py-0.5 rounded font-caption font-medium"
                    style={{ fontSize: "0.6875rem", color: sc?.color, background: sc?.bg, border: `1px solid ${sc?.border}` }}
                  >
                    {sc?.badge}
                  </span>
                </div>
                <p className="font-caption text-muted-foreground text-xs mb-1">{alert?.project}</p>
                <p className="font-caption text-muted-foreground" style={{ fontSize: "0.75rem", lineHeight: 1.5 }}>
                  {alert?.detail}
                </p>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="font-data text-muted-foreground" style={{ fontSize: "0.6875rem" }}>
                    {alert?.date}
                  </p>
                  <span
                    className="font-caption opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ fontSize: "0.6875rem", color: "var(--color-primary)" }}
                  >
                    View ESG Report →
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}